import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/api';
import { queryKeys } from '@/libs/queryKeys';
import { BatchedEchoStatsContext } from './BatchedEchoStatsContext';

/**
 * React Query for popularity / rating stats of one echo.
 *
 * When rendered inside a `<BatchedEchoStatsProvider>` that has stats
 * for this echo, the per-echo fetch is disabled and the batch data
 * is served synchronously via `initialData`. Disabling is required:
 * without it, siblings race the batch query and fire their own
 * per-echo requests before the batch can populate the shared cache,
 * restoring the N-way fanout the provider exists to prevent.
 *
 * Outside the provider, behaves as a plain `useQuery`.
 * Disabled when `echoId` is empty.
 */
export const useEchoStatsQuery = (echoId: string) => {
  const batchedMap = React.useContext(BatchedEchoStatsContext);
  const insideProvider = batchedMap !== null;
  const fromBatch = batchedMap?.get(echoId);

  return useQuery({
    queryKey: queryKeys.stats.echo(echoId),
    queryFn: () => api.ratings.getEchoStats(echoId),
    enabled: !!echoId && !insideProvider,
    ...(fromBatch ? { initialData: fromBatch } : {}),
  });
};
